import React, { useEffect, useRef, useState } from "react";

/**
 * Basit bir dikdörtgen seçim aracı:
 * - Görüntüyü <canvas>’a çizer
 * - Mouse ile sürükleyerek ROI (x,y,w,h) seçersin
 * - Seçimi bırakınca preview blob üretip parent'a onAddRoi ile gönderir
 * - Çoklu ROI destekli; highlightRoiId sadece stil için
 */
export default function ImageCropper({ imageUrl, onAddRoi, highlightRoiId }) {
  const canvasRef = useRef(null);
  const overlayRef = useRef(null);
  const imgRef = useRef(new Image());
  const [imgLoaded, setImgLoaded] = useState(false);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 }); // panning istersen genişletebilirsin

  // seçim state
  const [dragging, setDragging] = useState(false);
  const [start, setStart] = useState({ x: 0, y: 0 });
  const [rect, setRect] = useState(null); // {x,y,w,h}

  useEffect(() => {
    const img = imgRef.current;
    img.crossOrigin = "anonymous";
    img.onload = () => setImgLoaded(true);
    img.src = imageUrl;
    return () => {
      setImgLoaded(false);
    };
  }, [imageUrl]);

  useEffect(() => {
    if (!imgLoaded) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    // canvas boyut
    const containerWidth = canvas.parentElement.clientWidth;
    const containerHeight = Math.min(640, window.innerHeight - 220);
    canvas.width = containerWidth;
    canvas.height = containerHeight;

    // resmi sığdır (fit)
    const iw = imgRef.current.width;
    const ih = imgRef.current.height;
    const scaleFit = Math.min(containerWidth / iw, containerHeight / ih);
    setScale(scaleFit);
    setOffset({ x: (containerWidth - iw * scaleFit) / 2, y: (containerHeight - ih * scaleFit) / 2 });

    // ilk çizim
    draw(ctx, iw, ih, scaleFit, { x: (containerWidth - iw * scaleFit) / 2, y: (containerHeight - ih * scaleFit) / 2 }, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imgLoaded]);

  const draw = (ctx, iw, ih, sc, ofs, selection) => {
    // arka plan
    ctx.fillStyle = "#f2f6fb";
    ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);

    // görüntü
    ctx.imageSmoothingEnabled = true;
    ctx.drawImage(imgRef.current, ofs.x, ofs.y, iw * sc, ih * sc);

    // selection overlay
    if (selection) {
      const { x, y, w, h } = selection;
      ctx.save();
      ctx.strokeStyle = "#EA5B8B"; // pastel pembe kenar
      ctx.lineWidth = 2;
      ctx.setLineDash([6, 4]);
      ctx.strokeRect(x, y, w, h);

      ctx.fillStyle = "rgba(234,91,139,0.15)";
      ctx.fillRect(x, y, w, h);
      ctx.restore();
    }
  };

  const getPointerPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    return { x, y };
  };

  const handlePointerDown = (e) => {
    if (!imgLoaded) return;
    e.preventDefault();
    const pos = getPointerPos(e);
    setStart(pos);
    setRect({ x: pos.x, y: pos.y, w: 0, h: 0 });
    setDragging(true);
  };

  const handlePointerMove = (e) => {
    if (!dragging) return;
    e.preventDefault();
    const pos = getPointerPos(e);
    const x = Math.min(pos.x, start.x);
    const y = Math.min(pos.y, start.y);
    const w = Math.abs(pos.x - start.x);
    const h = Math.abs(pos.y - start.y);
    const sel = { x, y, w, h };
    setRect(sel);

    const ctx = canvasRef.current.getContext("2d");
    const iw = imgRef.current.width;
    const ih = imgRef.current.height;
    draw(ctx, iw, ih, scale, offset, sel);
  };

  const handlePointerUp = async (e) => {
    e.preventDefault();
    if (!dragging || !rect) return;
    setDragging(false);

    if (rect.w < 10 || rect.h < 10) return;

    const ix = Math.max(0, Math.round((rect.x - offset.x) / scale));
    const iy = Math.max(0, Math.round((rect.y - offset.y) / scale));
    const iw = Math.min(imgRef.current.width - ix, Math.round(rect.w / scale));
    const ih = Math.min(imgRef.current.height - iy, Math.round(rect.h / scale));

    const off = document.createElement("canvas");
    off.width = iw;
    off.height = ih;
    const octx = off.getContext("2d");
    octx.drawImage(imgRef.current, ix, iy, iw, ih, 0, 0, iw, ih);

    const blob = await new Promise((res) => off.toBlob(res, "image/png", 0.95));

    onAddRoi({ x: ix, y: iy, w: iw, h: ih, previewBlob: blob });

    const ctx = canvasRef.current.getContext("2d");
    draw(ctx, imgRef.current.width, imgRef.current.height, scale, offset, null);
    setRect(null);
  };

  useEffect(() => {
    // rect değişince overlay’i yeniden çiz
    if (!imgLoaded) return;
    const ctx = canvasRef.current.getContext("2d");
    draw(ctx, imgRef.current.width, imgRef.current.height, scale, offset, rect || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rect]);

  return (
    <div className="cropper">
      <canvas
        ref={canvasRef}
        className="cropper__canvas"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      />
      <div ref={overlayRef} className="cropper__overlay" />
    </div>
  );
}